import { compact } from '../common.utils';

/**
 * Matches the scanResult against the provided pattern and processes the result.
 *
 * @param {string} scanResult - The scanned barcode result.
 * @param {string | undefined} pattern - The pattern to match against the scanResult. If undefined, the scanResult is returned as is.
 * @returns {string} - The processed result after matching and applying any replacements specified by the pattern.
 * @throws {Error} - Throws an error if the pattern does not match the scanResult.
 */
export function matchAndProcessBarcode(scanResult: string, pattern: string | undefined): string {
    if (!pattern) {
        return scanResult;
    }
    const matchResult: string[] = compact(scanResult.match(pattern));
    if (matchResult?.length > 0 && matchResult[0] === scanResult) {
        // do any replacements that are given by the validation pattern, e.g. remove "20T" prefix
        if (matchResult.length > 1) {
            return matchResult[1];
        }
        return scanResult;
    }
    throw scanResult;
}

/**
 * Checks if a given text matches a specified pattern.
 *
 * This function takes a text string and a pattern string (which can be undefined),
 * and returns a boolean indicating whether the text matches the pattern.
 * If the pattern is undefined, the function returns true.
 * If the pattern is a valid regular expression and the text matches it, the function returns true.
 * If the pattern is a valid regular expression and the text does not match it, the function returns false.
 * If the pattern is an invalid regular expression, the function catches the error and returns false.
 *
 * @param {string} text - The text to be checked against the pattern.
 * @param {string | undefined} pattern - The pattern to check the text against. Can be undefined.
 * @returns {boolean} - true, if the text matches the pattern, or if the pattern is undefined.
 */
export function matchesPattern(text: string, pattern: string | undefined): boolean {
    try {
        matchAndProcessBarcode(text, pattern);
        return true;
    } catch {
        return false;
    }
}
